import type { ReactNode } from 'react';
import type { Contract, UsageEvent } from '@traceio-org/platform-contracts';
import type { PeriodUsage } from '@traceio-org/platform-client';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { formatMoney, formatTokens } from '../../../billing/money';
import { AdminProblem } from '../../../components/AdminProblem';
import { LoadingBlock, PagerButton } from '../../../components/feedback';
import { DateTimeText, ScrollTable, StatusBadge } from '../../../components/records';
import { useAdmin } from '../../../context';
import { actionLabel, formatCount, plural } from '../../../format';
import { intelligenceLevelLabel } from '../../../modules';
import { useAdminRead } from '../../../useAdminRead';
import { periodLabel } from './shared';

function Fact({ term, children, tone }: { term: string; children: ReactNode; tone?: 'danger' | 'warning' }) {
  return (
    <div>
      <dt>{term}</dt>
      <dd
        className={
          tone === 'danger' ? 'admin-over-cap' : tone === 'warning' ? 'admin-warning-text' : undefined
        }
      >
        {children}
      </dd>
    </div>
  );
}

function UsageFacts({ usage }: { usage: PeriodUsage }) {
  const cap = usage.spendCap;
  return (
    <dl className="admin-facts admin-facts--summary" aria-label="Usage of the period">
      <Fact term="Period">
        {periodLabel(usage.period)} <StatusBadge status={usage.status} />
      </Fact>
      <Fact term="Tokens billed">{formatTokens(usage.billableMillitokens)}</Fact>
      <Fact term="Tokens held">
        {formatTokens(usage.heldMillitokens)}
        <span className="admin-cell-secondary">Reserved by work in progress</span>
      </Fact>
      <Fact term="Tokens committed">
        {formatTokens(usage.committedMillitokens)}
        <span className="admin-cell-secondary">Billed plus held</span>
      </Fact>
      <Fact term="Shipments" tone={usage.beyondSelfServe ? 'warning' : undefined}>
        {formatCount(usage.shipments)}
        {usage.beyondSelfServe ? (
          <span className="admin-cell-secondary">
            Beyond the self-serve terms: an Enterprise prompt applies
          </span>
        ) : null}
      </Fact>
      {cap ? (
        <>
          <Fact term="Spend cap">{formatMoney(cap.capMicros, cap.currency)} a month</Fact>
          <Fact term="Cost so far">
            {formatMoney(cap.committedCostMicros, cap.currency)}
            <span className="admin-cell-secondary">The token price of billed plus held tokens</span>
          </Fact>
          <Fact term="Cap remaining" tone={cap.remainingMicros < 0 ? 'danger' : undefined}>
            {formatMoney(cap.remainingMicros, cap.currency)}
            {cap.remainingMicros < 0 ? (
              <span className="admin-cell-secondary">
                Over the cap: it was lowered after work was admitted
              </span>
            ) : null}
          </Fact>
        </>
      ) : (
        <Fact term="Spend cap">
          No cap
          <span className="admin-cell-secondary">
            The token line of the line items prices the billed tokens
          </span>
        </Fact>
      )}
    </dl>
  );
}

function ByActionTable({ usage }: { usage: PeriodUsage }) {
  if (usage.byAction.length === 0) return <p className="admin-muted">No usage in this period yet.</p>;
  return (
    <ScrollTable caption="Usage by action" className="admin-table admin-table--compact">
      <thead>
        <tr>
          <th scope="col">Action</th>
          <th scope="col">Module</th>
          <th scope="col" className="admin-num">
            Events
          </th>
          <th scope="col" className="admin-num">
            Tokens billed
          </th>
          <th scope="col">Included</th>
        </tr>
      </thead>
      <tbody>
        {usage.byAction.map((row) => (
          <tr key={`${row.action}:${row.moduleKey}`}>
            <th scope="row">{actionLabel(row.action)}</th>
            <td>
              <code>{row.moduleKey}</code>
            </td>
            <td className="admin-num">{formatCount(row.events)}</td>
            <td className="admin-num">{formatTokens(row.billableMillitokens)}</td>
            <td>
              {row.includedPerPeriod === null
                ? 'No allowance'
                : `${formatCount(row.includedQuantity)} of ${formatCount(row.includedPerPeriod)}`}
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

function EventsTable({
  events,
  caption,
  busy,
}: {
  events: readonly UsageEvent[];
  caption: string;
  busy: boolean;
}) {
  return (
    <ScrollTable caption={caption} busy={busy} className="admin-table admin-table--usage-events">
      <thead>
        <tr>
          <th scope="col">Occurred</th>
          <th scope="col">Action</th>
          <th scope="col">Module</th>
          <th scope="col">Level</th>
          <th scope="col" className="admin-num">
            Quantity
          </th>
          <th scope="col" className="admin-num">
            Estimated
          </th>
          <th scope="col" className="admin-num">
            Metered
          </th>
          <th scope="col" className="admin-num">
            Billable
          </th>
          <th scope="col">Reference</th>
          <th scope="col">Producer</th>
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event.id}>
            <th scope="row">
              <DateTimeText iso={event.occurredAt} />
              <span className="admin-cell-secondary">
                <code>{event.eventId}</code>
              </span>
            </th>
            <td>{actionLabel(event.action)}</td>
            <td>
              <code>{event.moduleKey}</code>
            </td>
            <td>
              {event.level ? intelligenceLevelLabel(event.level) : <span className="admin-muted">None</span>}
            </td>
            <td className="admin-num">{formatCount(event.quantity)}</td>
            <td className="admin-num">{formatTokens(event.estimatedMillitokens)}</td>
            <td className="admin-num">
              {event.meteredMillitokens === null ? (
                <span className="admin-muted">Not metered</span>
              ) : (
                formatTokens(event.meteredMillitokens)
              )}
            </td>
            <td className="admin-num">{formatTokens(event.billableMillitokens)}</td>
            <td>
              {event.reference.type} <code>{event.reference.id}</code>
            </td>
            <td>
              <code>{event.producer}</code>
              {event.reservationId ? (
                <span className="admin-cell-secondary">Settled from a reservation</span>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

export interface UsageSectionProps {
  organizationId: string;
  contract: Contract;
  /** The period's start, or undefined for the current period. */
  periodStart: string | undefined;
  cursor: string | undefined;
  /** The cursors of the pages before this one, from the history state; null when the page was opened directly. */
  previous: string[] | null;
  onPage: (cursor: string | undefined, previous: string[] | null) => void;
}

/**
 * The usage of one period of a contract: tokens billed, held and committed, the spend cap against the cost so far,
 * the usage by action, and the usage events page by page (keyset cursor in the address). Every number is
 * platform-api's; the browser only changes units.
 */
export function UsageSection({
  organizationId,
  contract,
  periodStart,
  cursor,
  previous,
  onPage,
}: UsageSectionProps) {
  const { api } = useAdmin();
  const key = `usage\u0000${organizationId}\u0000${contract.id}\u0000${periodStart ?? ''}\u0000${cursor ?? ''}`;
  const read = useAdminRead(key, (signal) =>
    api.getBillingUsage(
      organizationId,
      {
        contractId: contract.id,
        ...(periodStart ? { periodStart } : {}),
        ...(cursor ? { cursor } : {}),
      },
      signal,
    ),
  );
  const current = read.dataKey === key && !read.failure ? read.data : null;
  const shown = read.failure ? null : read.data;
  const pageNumber = previous ? previous.length + 1 : null;

  let body: ReactNode;
  if (read.failure?.kind === 'not-found') {
    body = (
      <EmptyPageState
        icon={<NavigationGlyph name="Activity" />}
        title="No usage period"
        description="The contract has no period yet (it has not started), or no period of it starts at the chosen date."
      />
    );
  } else if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the usage" onRetry={read.retry} />;
  } else if (!shown) {
    body = <LoadingBlock label="Loading the usage" />;
  } else {
    const events = shown.events;
    const nextCursor = current?.nextCursor ?? null;
    const showPager = cursor !== undefined || nextCursor !== null;
    body = (
      <div className="admin-stack">
        <UsageFacts usage={shown} />
        <h3 className="admin-subheading">By action</h3>
        <ByActionTable usage={shown} />
        <h3 className="admin-subheading">Usage events</h3>
        <p className="admin-results-status" role="status" aria-live="polite">
          {read.loading
            ? 'Loading usage events'
            : current
              ? `${plural(events.length, 'event', 'events')} on this page${pageNumber && (pageNumber > 1 || nextCursor) ? `, page ${formatCount(pageNumber)}` : ''}`
              : ''}
        </p>
        {events.length === 0 ? (
          <p className="admin-muted">No usage event in this period.</p>
        ) : (
          <EventsTable
            events={events}
            busy={read.loading}
            caption={
              pageNumber && (pageNumber > 1 || nextCursor)
                ? `Usage events, page ${formatCount(pageNumber)}`
                : 'Usage events'
            }
          />
        )}
        {showPager ? (
          <div className="admin-pager">
            {previous ? (
              <PagerButton
                onClick={() => {
                  if (!previous || previous.length === 0) return;
                  const before = previous[previous.length - 1];
                  onPage(before || undefined, previous.slice(0, -1));
                }}
                disabled={previous.length === 0 || read.loading}
              >
                Previous page
              </PagerButton>
            ) : (
              <PagerButton onClick={() => onPage(undefined, null)} disabled={read.loading}>
                First page
              </PagerButton>
            )}
            <PagerButton
              onClick={() => {
                if (nextCursor) onPage(nextCursor, previous ? [...previous, cursor ?? ''] : null);
              }}
              disabled={nextCursor === null || read.loading}
            >
              Next page
            </PagerButton>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <PageSection
      title="Usage"
      description="What the period used: tokens billed and still held, the spend cap against the cost so far, by action, and every usage event."
    >
      {body}
    </PageSection>
  );
}
